import { createClient } from '@supabase/supabase-js'
import '../vite-env.d'; // Import our environment variable type definitions
import type { Database } from './database.types'
import { SITE_CONFIG } from './constants'

// Routes that require database access
const DB_DEPENDENT_PATHS = [
  '/survey/create',
  '/survey/results',
  '/survey/manage',
  '/survey/insights', 
  '/auth'
];

// Explicitly add routes that should never attempt auth refresh
const PUBLIC_ROUTES = ['/', '/services', '/our-approach', '/meet-the-team', '/success-stories', '/insights', '/privacy', '/terms', '/contact', '/ai-kickstarter'];

// Check if the current route is explicitly public (no auth needed)
function isPublicRoute() {
  const currentPath = window.location.pathname;
  return PUBLIC_ROUTES.includes(currentPath) || 
         PUBLIC_ROUTES.some(route => currentPath.startsWith(route) && !currentPath.includes('/survey/'));
}

// Check if the current route requires database access
function isProtectedRoute() {
  const currentPath = window.location.pathname;
  return DB_DEPENDENT_PATHS.some(route => 
    currentPath.startsWith(route) || 
    (currentPath.includes('/survey/') && !currentPath.includes('/thank-you'))
  );
}

// Create a global state for database availability
let isDatabaseOffline = SITE_CONFIG.FORCE_DATABASE_OFFLINE || false;
let connectionCheckCount = 0;
const CONNECTION_CHECK_INTERVAL = 60000; // Check every minute
const MAX_CONNECTION_CHECKS = 3; // Maximum number of times to check before giving up
const BACKOFF_INTERVAL = 5 * 60000; // 5 minutes between retries after max checks

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Missing Supabase environment variables')
  isDatabaseOffline = true
}

// Store the original fetch function
const originalFetch = window.fetch;

// Mock Supabase's response format for errors
const createMockResponse = (message = 'Database is offline') => {
  return new Response(
    JSON.stringify({
      error: {
        message,
        code: 'offline'
      }
    }),
    {
      status: 503,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
};

// Export a function to manually set the offline state
export function setDatabaseOfflineState(offline: boolean): void {
  isDatabaseOffline = offline;
  if (!offline) {
    // Reset connection check count when manually setting to online
    connectionCheckCount = 0;
  }
  
  // Only log when running on protected routes
  if (isProtectedRoute()) {
    console.log(`Database offline state set to: ${offline}`);
  }
}

// Replace the global fetch with our interceptor
window.fetch = function(...args) {
  const url = args[0].toString();
  
  // For auth refresh calls on public routes, return a mock successful response
  // to prevent unnecessary token refresh attempts
  if (isPublicRoute() && url.includes('supabase.co') && url.includes('/auth/v1/token?grant_type=refresh_token')) {
    return Promise.resolve(new Response(
      JSON.stringify({ 
        access_token: 'mock-token-for-public-page',
        refresh_token: 'mock-refresh-token-for-public-page',
        expires_in: 3600,
        token_type: 'bearer'
      }),
      {
        status: 200,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    ));
  }
  
  // Skip interception for non-protected routes
  if (!isProtectedRoute()) {
    return originalFetch.apply(window, args);
  }
  
  // Check if this is a Supabase request
  if (url.includes('supabase.co')) {
    // If database is already marked as offline, return mock response
    if (isDatabaseOffline) {
      // Only log message for first few attempts to avoid console spam
      if (connectionCheckCount < MAX_CONNECTION_CHECKS) {
        console.log(`Intercepted Supabase request while offline: ${url}`);
      }
      return Promise.resolve(createMockResponse());
    }
    
    // Otherwise, attempt the fetch
    return originalFetch.apply(window, args)
      .catch(error => {
        // If network error occurs, mark database as offline
        console.error('Fetch error detected. Setting database to offline mode.');
        isDatabaseOffline = true;
        connectionCheckCount++; // Increment counter
        return createMockResponse();
      });
  }
  
  // For non-Supabase requests, use original fetch
  return originalFetch.apply(window, args);
};

// Create the Supabase client
const supabaseClient = createClient<Database>(
  supabaseUrl || 'https://example.com', // Fallback URL when none provided
  supabaseAnonKey || 'fallback-key'
)

// Create a proxy to intercept Supabase calls when offline
export const supabase = new Proxy(supabaseClient, {
  get(target, prop, receiver) {
    const original = Reflect.get(target, prop, receiver)
    
    // Skip interception on non-protected routes
    if (!isProtectedRoute()) {
      return original;
    }
    
    // If database is offline and the property is a function, return a mock function
    if (isDatabaseOffline && typeof original === 'function') {
      return (...args: any[]) => {
        if (connectionCheckCount < 3) {
          console.log(`Database is offline. Skipped call to supabase.${String(prop)}()`)
        }
        return Promise.resolve({ data: null, error: { message: 'Database is offline', code: 'offline' } })
      }
    }
    
    // If the property is an object (like .from(), .auth(), etc.), wrap it too
    if (isDatabaseOffline && typeof original === 'object' && original !== null) {
      return new Proxy(original, {
        get(obj, method, objReceiver) {
          const originalMethod = Reflect.get(obj, method, objReceiver)
          
          if (typeof originalMethod === 'function') {
            return (...args: any[]) => {
              if (connectionCheckCount < 3) {
                console.log(`Database is offline. Skipped call to supabase.${String(prop)}.${String(method)}()`)
              }
              return {
                data: null, 
                error: { message: 'Database is offline', code: 'offline' },
                // Support for chained methods
                then: (callback: any) => Promise.resolve({ data: null, error: { message: 'Database is offline', code: 'offline' } }).then(callback),
                catch: (callback: any) => Promise.resolve({ data: null, error: { message: 'Database is offline', code: 'offline' } }).catch(callback),
                select: () => ({ data: null, error: { message: 'Database is offline', code: 'offline' } }),
                eq: () => ({ data: null, error: { message: 'Database is offline', code: 'offline' } }),
                single: () => ({ data: null, error: { message: 'Database is offline', code: 'offline' } }),
                limit: () => ({ data: null, error: { message: 'Database is offline', code: 'offline' } })
              }
            }
          }
          
          return originalMethod
        }
      })
    }
    
    return original
  }
})

// Function to check database connection
export async function checkConnection(): Promise<boolean> {
  // Skip checks on non-protected routes
  if (!isProtectedRoute()) {
    return true;
  }
  
  // Increment the check counter
  connectionCheckCount++;
  
  // If we've already checked too many times, wait longer before trying again
  if (connectionCheckCount > MAX_CONNECTION_CHECKS) {
    // Only log once per backoff period
    if (connectionCheckCount === MAX_CONNECTION_CHECKS + 1) {
      console.log(`Maximum connection attempts reached. Backing off for ${BACKOFF_INTERVAL/60000} minutes.`);
    }
    return false;
  }
  
  // Check if we're forcing offline mode
  if (SITE_CONFIG.FORCE_DATABASE_OFFLINE) {
    isDatabaseOffline = true
    return false
  }

  // Skip check if offline detection is disabled
  if (!SITE_CONFIG.DATABASE_OFFLINE_DETECTION) {
    return true
  }
  
  // If already offline, don't spam with repeated checks
  if (isDatabaseOffline && connectionCheckCount > 1) {
    return false;
  }

  try {
    // Try a simple query to check connection
    const { error } = await supabaseClient
      .from('roles')
      .select('id')
      .limit(1)

    if (error) {
      if (!isDatabaseOffline) {
        console.warn('Database connection error - switching to offline mode')
      }
      isDatabaseOffline = true
      return false
    }

    // Connection successful, update the state
    console.log('Database connection successful')
    isDatabaseOffline = false
    connectionCheckCount = 0; // Reset counter on success
    return true
  } catch (error) {
    if (!isDatabaseOffline) {
      console.warn('Failed to check database connection - switching to offline mode')
    }
    isDatabaseOffline = true
    return false
  }
}

// Set up periodic connection checking if enabled
if (SITE_CONFIG.DATABASE_OFFLINE_DETECTION) {
  // Check immediately on load only if on protected route
  if (isProtectedRoute()) {
    checkConnection().then(isOnline => {
      console.log(`Database is ${isOnline ? 'online' : 'offline'}`);
    });
  }

  // Set up interval checking - use a tiered approach
  setInterval(() => {
    // Only run checks on protected routes
    if (!isProtectedRoute()) {
      return;
    }
    
    // If we've exceeded our check count, use the backoff interval
    if (connectionCheckCount > MAX_CONNECTION_CHECKS) {
      // Reset counter after the backoff period
      connectionCheckCount = 0;
    } else {
      checkConnection();
    }
  }, CONNECTION_CHECK_INTERVAL);
}

export async function validateSurveyCode(code: string) {
  const { data, error } = await supabase
    .from('survey_codes')
    .select('code, expires_at')
    .eq('code', code)
    .single()

  return data
}